import type { ActionType, GameEvent, GamePhase } from '@/store/useGameStore';
import type { MatchMode } from '@/utils/matchCycle';
import { buildEnabledActionsSet, filterActionsForLiveMode, type CycleAction } from '@/utils/matchCycle';
import { getTargetStatus } from '@/components/match-widgets/expert-court/constants';
import { isPhantomEvent } from '@/store/game/rallyPredicates';

/** Actions de rallye affichées dans les sélecteurs (hors fautes, temps mort, etc.). */
export const RALLY_ACTION_TYPES: ReadonlyArray<ActionType> = [
  'Service',
  'Réception',
  'Passe',
  'Attaque',
  'Bloc',
  'Défense',
  'Freeball Donnée',
  'Freeball Reçue',
  'Retour Direct',
];

const RALLY_ACTION_SET = new Set<string>(RALLY_ACTION_TYPES);

function isRallyActionType(value: string): value is ActionType {
  return RALLY_ACTION_SET.has(value);
}

export interface VisibleMatchActionsInput {
  gamePhase: GamePhase;
  scoreUs: number;
  scoreThem: number;
  currentPointEventsLength: number;
  /** Événements du point en cours (pour règles dépendant du dernier geste, ex. soutien après attaque bloquée). */
  currentPointEvents?: readonly GameEvent[] | null;
  selectedPlayer: string | null;
  court: string[];
  /** Positions affichées (permutation tactique / libéro) ; sinon avant/arrière basé sur `court`. */
  visualCourt?: readonly string[] | null;
  libero: string | null;
  matchMode: MatchMode;
  enabledActions: readonly CycleAction[];
  /** Saisie deux équipes : seule elle enchaîne sur le bloc de l'équipe d'en face. */
  dualTeamCapture?: boolean;
}

/** Attaque « bloquée » sur notre terrain (coordonnées expert) puis qualité soutien ! / - : pas de phase Bloc collective. */
export function isOurBlockedAttackSupportDigEvent(ev: GameEvent): boolean {
  if (ev.isOpponent) return false;
  if (ev.action !== 'Attaque') return false;
  if (ev.quality !== '!' && ev.quality !== '-') return false;
  if (ev.targetX === undefined || ev.targetY === undefined) return false;
  return getTargetStatus(ev.targetX, ev.targetY, 'Attaque') === 'BLOCKED';
}

/**
 * Attaque **renvoyée par le contre dans le camp de l'attaquant** (cible marquée
 * `BLOCKED` du point de vue de celui qui attaque).
 *
 * Le ballon repart donc du côté de l'attaquant : c'est son équipe qui le
 * soutient (couverture), pas celle qui vient de bloquer. En deux équipes, cette
 * traversée « retour » doit faire rebasculer l'équipe active après la
 * désignation du bloqueur — sans quoi on enchaînait sur la défense de l'équipe
 * qui bloque, restée en saisie.
 */
export function isAttackBlockedBackToAttacker(ev: GameEvent | undefined): boolean {
  if (!ev || ev.action !== 'Attaque') return false;
  if (ev.targetX === undefined || ev.targetY === undefined) return false;
  const side: 'us' | 'them' =
    ev.isOpponent === true || ev.teamSide === 'away' ? 'them' : 'us';
  return getTargetStatus(ev.targetX, ev.targetY, 'Attaque', side) === 'BLOCKED';
}

/**
 * Suite de gestes du rallye telle que la dérivation de l'équipe active
 * (`deriveActiveTeam`) doit la lire : un `Bloc` qui suit une attaque renvoyée
 * dans le camp de l'attaquant est marqué comme renvoyant le ballon, pour que
 * l'undo et la reprise retombent sur la même équipe active que la saisie.
 */
export function toDualRallySteps(
  events: readonly GameEvent[],
): { action: string; blockReturnsBall?: boolean; quality?: string }[] {
  return events.map((ev, i) => {
    // La qualité voyage avec le geste : un « / » sur une réception, une défense
    // ou une freeball reçue renvoie le ballon en face, et la dérivation doit le
    // voir pour retomber sur la même équipe active que la saisie.
    if (ev.action !== 'Bloc') return { action: ev.action, quality: ev.quality };
    const previous = lastNonPhantomEvent(events.slice(0, i));
    return {
      action: 'Bloc',
      quality: ev.quality,
      // "!" = covered block: sent back to the attacker, even without an expert target (VOL-189).
      blockReturnsBall: isAttackBlockedBackToAttacker(previous) || ev.quality === '!',
    };
  });
}

/**
 * Le bloqueur doit-il être désigné avant toute autre action ?
 *
 * En deux équipes, une attaque dont la cible est marquée « touchée » est passée
 * DANS le bloc : le store enchaîne sur l'action `Bloc` pour en désigner l'auteur.
 * Mais la phase `Block` proposait aussi Défense / Freeball / Retour direct, si
 * bien qu'on pouvait enchaîner sans jamais nommer le bloqueur — et l'information
 * « qui a touché » était perdue, alors que c'est précisément ce que la cible
 * venait de déclarer.
 *
 * Restreint au mode deux équipes : en une équipe, la phase `Block` qui suit notre
 * attaque désigne NOTRE bloc sur la contre-attaque adverse, pas le bloc qui vient
 * de toucher notre ballon. Et sans `Bloc` dans les actions suivies, il n'y a rien
 * à désigner.
 */
export function mustDesignateBlocker(
  lastEvent: GameEvent | undefined,
  enabledSet: ReadonlySet<string>,
  dualTeamCapture: boolean,
): boolean {
  if (!dualTeamCapture || !enabledSet.has('Bloc')) return false;
  return lastEvent?.action === 'Attaque' && lastEvent.touchedByBlock === true;
}

export function lastNonPhantomEvent(events: readonly GameEvent[] | null | undefined): GameEvent | undefined {
  if (!events?.length) return undefined;
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (!isPhantomEvent(e)) return e;
  }
  return undefined;
}

/**
 * Liste d'actions affichables selon la phase de jeu, le joueur sélectionné (avant / arrière)
 * et le mode (live : filtre `enabledActions`, vidéo : toutes les actions de phase).
 */
export function computeVisibleMatchActions({
  gamePhase,
  scoreUs,
  scoreThem,
  currentPointEventsLength,
  currentPointEvents,
  selectedPlayer,
  court,
  visualCourt,
  libero,
  matchMode,
  enabledActions,
  dualTeamCapture = false,
}: VisibleMatchActionsInput): ActionType[] {
  const isStartOfSet = scoreUs === 0 && scoreThem === 0 && currentPointEventsLength === 0;

  if (isStartOfSet) {
    return ['Service', 'Réception'];
  }

  const rowCourt = visualCourt && visualCourt.length >= 6 ? visualCourt : court;

  let isBackRow = false;
  if (selectedPlayer) {
    if (selectedPlayer === libero) {
      isBackRow = true;
    } else {
      const idx = rowCourt.indexOf(selectedPlayer);
      if (idx !== -1 && [0, 4, 5].includes(idx)) {
        isBackRow = true;
      }
    }
  }

  const enabledSet = buildEnabledActionsSet(enabledActions);

  let phaseActions: ReadonlyArray<ActionType>;
  switch (gamePhase) {
    case 'Serve':
      phaseActions = ['Service'];
      break;
    case 'Reception':
      phaseActions = ['Réception'];
      break;
    case 'Set':
      phaseActions = ['Passe', 'Attaque', 'Freeball Donnée'];
      break;
    case 'Attack':
      phaseActions = ['Attaque', 'Freeball Donnée'];
      break;
    case 'Block': {
      const last = lastNonPhantomEvent(currentPointEvents ?? undefined);
      const supportOnly = last ? isOurBlockedAttackSupportDigEvent(last) : false;
      if (mustDesignateBlocker(last, enabledSet, dualTeamCapture)) {
        phaseActions = ['Bloc'];
      } else if (supportOnly) {
        phaseActions = ['Défense', 'Freeball Reçue', 'Retour Direct'];
      } else {
        phaseActions = isBackRow
          ? ['Défense', 'Freeball Reçue', 'Retour Direct']
          : ['Bloc', 'Défense', 'Freeball Reçue', 'Retour Direct'];
      }
      break;
    }
    case 'Dig': {
      const lastDig = lastNonPhantomEvent(currentPointEvents ?? undefined);
      const afterBlockedSupport =
        lastDig && isOurBlockedAttackSupportDigEvent(lastDig);
      phaseActions = afterBlockedSupport
        ? ['Défense', 'Passe', 'Retour Direct']
        : ['Défense', 'Freeball Donnée', 'Passe', 'Retour Direct'];
      break;
    }
    default:
      phaseActions = RALLY_ACTION_TYPES;
  }

  if (matchMode === 'live') {
    return filterActionsForLiveMode([...phaseActions], enabledSet).filter(isRallyActionType);
  }

  return [...phaseActions];
}
